import {
  biChartUnit,
  biChartValue,
  BiDisplayFormat,
  formatBiValue,
} from '@euler/model/bi/format';
import { BiCell, BiFieldType, BiQueryResult } from '@euler/model/bi/result';
import {
  BiAxisSide,
  BiChartViz,
  BiPalette,
  BiSeriesType,
  biSeriesTypeOf,
  BiVizSeries,
  BiVizType,
  kBiMaxRadarSeries,
  kBiPyramidSides,
} from '@euler/model/bi/viz';
import { biSeriesColor } from './biColors';
import { biColumnLookup, BiColumnRef } from './biColumns';
import { biBucketGrainOf, biWindowBuckets } from './biPeriodBuckets';
import { biCategoryLabel, biPivot } from './biPivot';
import { biText } from './biText';

/** One drawn series: a measure, or one value of the `seriesBy` dimension. */
export interface BiChartSeries {
  /** What `style.seriesColors` names it by. */
  key: string;
  name: string;
  type: BiSeriesType;
  axis: BiAxisSide;
  color: string;
  /** In display units (yuan, percentage points); undefined is a gap. */
  values: (number | undefined)[];
  /** Each value as its data label reads. */
  labels: string[];
  format?: BiDisplayFormat;
}

export interface BiPieSlice {
  /** The value as text: what `style.seriesColors` names it by. */
  key: string;
  name: string;
  value: number;
  label: string;
  color: string;
}

/** A radar or a pyramid: series over categories, some maybe not drawn. */
interface BiSidedModel {
  categories: string[];
  series: BiChartSeries[];
  /** The unit the values are in, when the series share one. */
  units: { left: string | null; right: null };
  legend: boolean;
  /** Split series beyond what the chart draws (the table shows them). */
  hidden: number;
}

export type BiChartModel =
  | {
      kind: 'cartesian';
      type: Exclude<
        BiVizType,
        BiVizType.Pie | BiVizType.Donut | BiVizType.Radar | BiVizType.Pyramid
      >;
      categories: string[];
      series: BiChartSeries[];
      /** Left bars stack (a stacked bar chart). */
      stacked: boolean;
      /** The unit each axis is in, when its series share one. */
      units: { left: string | null; right: string | null };
      /** Several series, or a blend's sides: the legend shows by default. */
      legend: boolean;
      /** The categories are time buckets, read in sequence. */
      timeAxis: boolean;
    }
  | (BiSidedModel & {
      kind: 'radar';
      /** Each series against its own maximum: units or sizes too far apart. */
      perSeries: boolean;
    })
  | (BiSidedModel & { kind: 'pyramid' })
  | {
      kind: 'pie';
      slices: BiPieSlice[];
      /** The total, as the donut's center reads it. */
      total: string;
      unit: string | null;
    };

function isTimeColumn(ref: BiColumnRef): boolean {
  return (
    ref.column.type === BiFieldType.Date ||
    ref.column.type === BiFieldType.Datetime
  );
}

/** The title of a pyramid side no row names. */
const kBiNoDataSide = '无数据';

/**
 * The two sides of a pyramid split by a dimension. A split by period puts
 * the earlier period on the left, a two-value code keeps its codes' order,
 * whichever the rows listed first. A side with no rows keeps its place and
 * its title: the other period of a two-period window, the other value of
 * the code, else a side titled as having no data.
 */
function pyramidSides(
  result: BiQueryResult,
  by: BiColumnRef,
  present: string[],
): string[] {
  const grain = biBucketGrainOf(by.column);
  const codes = Object.keys(by.column.valueLabels ?? {});
  if (
    !grain &&
    codes.length === kBiPyramidSides &&
    present.length &&
    present.every(value => codes.includes(value))
  )
    return codes;
  if (present.length !== 1)
    return grain || isTimeColumn(by)
      ? [...present].sort(timeAscending)
      : present;
  const periods = grain
    ? biWindowBuckets(result.window, grain, kBiPyramidSides + 1)
    : [];
  if (periods.length === kBiPyramidSides && periods.includes(present[0]))
    return periods;
  return [present[0], kBiNoDataSide];
}

/** Time bucket labels sort as text; the empty bucket goes last. */
function timeAscending(left: string, right: string): number {
  const empty = biCategoryLabel(null);
  if (left === right) return 0;
  if (left === empty) return 1;
  if (right === empty) return -1;
  return left < right ? -1 : 1;
}

function sharedUnit(series: BiChartSeries[]): string | null {
  const units = new Set(series.map(entry => biChartUnit(entry.format)));
  return units.size === 1 ? [...units][0] : null;
}

function pie(
  viz: BiChartViz,
  result: BiQueryResult,
  x: BiColumnRef,
  measure: BiColumnRef,
  palette: BiPalette | undefined,
): BiChartModel {
  const total = result.rows.reduce(
    (sum, row) => sum + (Number(row[measure.index]) || 0),
    0,
  );
  return {
    kind: 'pie',
    slices: result.rows.map((row, i) => {
      const key = biCategoryLabel(row[x.index]);
      return {
        key,
        name: biCategoryLabel(row[x.index], x.column.valueLabels),
        // A slice cannot be negative: it draws as nothing.
        value: Math.max(
          biChartValue(row[measure.index], measure.format) ?? 0,
          0,
        ),
        label: formatBiValue(row[measure.index], measure.format),
        color: biSeriesColor(viz.style, palette, key, i),
      };
    }),
    total: formatBiValue(total, measure.format),
    unit: biChartUnit(measure.format),
  };
}

function seriesOf(
  viz: BiChartViz,
  palette: BiPalette | undefined,
  /** The viz series drawn: its type and axis (a `seriesBy` split's too). */
  entry: BiVizSeries,
  key: string,
  name: string,
  index: number,
  measure: BiColumnRef,
  cells: BiCell[],
): BiChartSeries {
  return {
    key,
    name,
    type: biSeriesTypeOf(viz, entry),
    axis: entry.axis ?? BiAxisSide.Left,
    color: biSeriesColor(viz.style, palette, key, index),
    values: cells.map(cell => biChartValue(cell, measure.format)),
    labels: cells.map(cell => formatBiValue(cell, measure.format)),
    format: measure.format,
  };
}

/**
 * A chart viz over its result, as the renderer draws it: categories,
 * series with their colors (the card's own, else its palette, else the
 * dashboard's), units. Null when the viz names a column the result lacks.
 */
export function biChartModel(
  viz: BiChartViz,
  result: BiQueryResult,
  /** The dashboard's palette; a chat draft has none. */
  palette?: BiPalette,
): BiChartModel | null {
  const column = biColumnLookup(result, viz.formats);
  const x = column(viz.x);
  const measures = viz.series.map(entry => column(entry.key));
  if (!x || measures.length === 0) return null;
  if (measures.some(measure => measure === null)) return null;
  const refs = measures as BiColumnRef[];
  if (viz.type === BiVizType.Pie || viz.type === BiVizType.Donut)
    return pie(viz, result, x, refs[0], palette);

  let categories: string[] = [];
  let drawn: BiChartSeries[] = [];
  if (viz.seriesBy) {
    const by = column(viz.seriesBy);
    if (!by) return null;
    const pivot = biPivot(result, x, by, refs[0]);
    const { rows } = pivot;
    const groups =
      viz.type === BiVizType.Pyramid
        ? pyramidSides(result, by, pivot.groups)
        : pivot.groups;
    categories = rows.map(row => row.category);
    drawn = groups.map((group, i) =>
      seriesOf(
        viz,
        palette,
        viz.series[0],
        group,
        pivot.names.get(group) ?? biCategoryLabel(group, by.column.valueLabels),
        i,
        refs[0],
        rows.map(row => row.cells.get(group) ?? null),
      ),
    );
  } else {
    categories = result.rows.map(row =>
      biCategoryLabel(row[x.index], x.column.valueLabels),
    );
    drawn = refs.map((measure, i) =>
      seriesOf(
        viz,
        palette,
        viz.series[i],
        viz.series[i].key,
        viz.series[i].name ?? biText(measure.column.label),
        i,
        measure,
        result.rows.map(row => row[measure.index]),
      ),
    );
  }
  // A blend's measures are named by their dataset (the server's
  // `<measure>（<dataset>）` labels): that legend always shows.
  const blended = refs.some(ref => ref.column.side !== undefined);
  if (viz.type === BiVizType.Radar || viz.type === BiVizType.Pyramid)
    return sided(viz.type, categories, drawn, blended, !!viz.seriesBy);
  const left = drawn.filter(entry => entry.axis === BiAxisSide.Left);
  const right = drawn.filter(entry => entry.axis === BiAxisSide.Right);
  return {
    kind: 'cartesian',
    type: viz.type,
    stacked: viz.type === BiVizType.StackedBar,
    categories,
    series: drawn,
    units: {
      left: sharedUnit(left),
      right: right.length ? sharedUnit(right) : null,
    },
    legend: drawn.length > 1 || blended,
    timeAxis: isTimeColumn(x),
  };
}

/** Largest over smallest series peak past which one scale squashes some. */
const kRadarScaleSpread = 10;

function peak(series: BiChartSeries): number {
  return Math.max(
    0,
    // Negatives draw at the center: only the positive reach counts.
    ...series.values.map(value => (value === undefined ? 0 : value)),
  );
}

/**
 * A radar draws its first five series, a pyramid its first two (left, then
 * right). A radar of measures whose units differ, or whose peaks are an
 * order of magnitude apart, draws each against its own maximum.
 */
function sided(
  type: BiVizType.Radar | BiVizType.Pyramid,
  categories: string[],
  all: BiChartSeries[],
  blended: boolean,
  split: boolean,
): BiChartModel {
  const cap = type === BiVizType.Radar ? kBiMaxRadarSeries : kBiPyramidSides;
  const series = all.slice(0, cap).map(entry => ({
    ...entry,
    type: type === BiVizType.Radar ? BiSeriesType.Line : BiSeriesType.Bar,
  }));
  const base = {
    categories,
    series,
    units: { left: sharedUnit(series), right: null },
    hidden: all.length - series.length,
  };
  if (type === BiVizType.Pyramid)
    // Each side is titled above its half: no legend unless asked for.
    return { ...base, kind: 'pyramid', legend: false };
  const peaks = series.map(peak).filter(value => value > 0);
  const unitsDiffer =
    new Set(series.map(entry => biChartUnit(entry.format))).size > 1;
  const spread =
    peaks.length > 1 &&
    Math.max(...peaks) >= kRadarScaleSpread * Math.min(...peaks);
  const perSeries = !split && (unitsDiffer || spread);
  return {
    ...base,
    kind: 'radar',
    perSeries,
    legend: series.length > 1 || blended,
  };
}
